// Логический модуль хука useUpdateSiteParams (гейт hook-logic-in-logic-module):
// правило имени файла берёт часть имени до первой точки и ждёт экспорт с таким
// именем, а многофункциональный .logic-модуль по построению его не имеет.
/* eslint-disable canon/file-name-matches-primary-export -- см. комментарий выше: ни один экспорт не может носить имя хука, чей логикой этот модуль является */
import { siteParameterNames } from "@link-factory/shared/SiteParameterNames";
import type { paths } from "../../api/generated/api-types";
import type {
  CopyStatus,
  CopyTarget,
  UpdateParameter,
  UpdateParamsSummary,
  UpdateRequestState,
} from "./UpdateParamsDialog";

export type UpdateSiteParamsRequest =
  paths["/sites/params/update"]["post"]["requestBody"]["content"]["application/json"];

export type UpdateSiteParamsResponse =
  paths["/sites/params/update"]["post"]["responses"]["200"]["content"]["application/json"];

export const PERMISSION_REFUSAL_MESSAGE =
  "Недостаточно прав на обновление параметров сайтов: повтор не поможет — обратитесь к администратору за правом обновлять параметры сайтов";
export const TRANSACTION_ABORTED_MESSAGE =
  "Значения не записаны, ни один сайт не изменён. Список сохранён в поле — повторите обновление";
/** Отказ 2a без причины от backend'а: класс причины называется здесь, по-русски. */
export const VALIDATION_REFUSAL_MESSAGE =
  "Запрос отклонён: набор параметров пуст, содержит повтор или неизвестное значение";

// Общий текст зод-ветки backend'а: причины в нём нет, и на экран он не выносится.
const GENERIC_VALIDATION_REASON = "Validation failed";
const REJECTED_LIST_NEXT_STEP = " — исправьте список и отправьте снова";

/** Автомат диалога обновления параметров — раздел «Переходы» экрана SiteList. */
export interface UpdateSiteParamsState {
  open: boolean;
  request: UpdateRequestState;
  /** Отмеченные параметры в порядке dr, organicKeywords, age. */
  selectedParameters: UpdateParameter[];
  rawData: string;
  /** Сводка ответа SITE-24; null во всех состояниях, кроме result. */
  summary: UpdateParamsSummary | null;
  /** Сообщение отказа над формой; null во всех состояниях, кроме error. */
  errorMessage: string | null;
  /** Статус «Скопировать» списка ненайденных адресов. */
  copyNotFoundStatus: CopyStatus;
  /** Статус «Скопировать» списка невалидных строк. */
  copyInvalidStatus: CopyStatus;
}

const ALL_PARAMETERS: UpdateParameter[] = [...siteParameterNames];

// Имена ячеек строки в подсказке формата, в каноническом порядке siteParameterNames.
const CELL_LABELS: Record<UpdateParameter, string> = {
  dr: "dr",
  organicKeywords: "organic_kw",
  age: "age",
};

const PARAMETER_CELLS: { key: UpdateParameter; cell: string }[] = ALL_PARAMETERS.map((key) => ({
  key,
  cell: CELL_LABELS[key],
}));

/**
 * Диалог закрыт; открытие начинает ввод заново — введённый список нигде не сохраняется.
 * Ни один параметр не отмечен: набор выбирает пользователь по составу своего списка.
 */
export const CLOSED_UPDATE_STATE: UpdateSiteParamsState = {
  open: false,
  request: "input",
  selectedParameters: [],
  rawData: "",
  summary: null,
  errorMessage: null,
  copyNotFoundStatus: "idle",
  copyInvalidStatus: "idle",
};

export function openedDialog(): UpdateSiteParamsState {
  return { ...CLOSED_UPDATE_STATE, open: true };
}

/** Подсказка формата строки по набору: «site, dr», «site, dr, age», «site, dr, organic_kw, age». */
export function lineFormatFor(parameters: UpdateParameter[]): string {
  const cells = PARAMETER_CELLS.filter((entry) => parameters.includes(entry.key)).map(
    (entry) => entry.cell,
  );
  return ["site", ...cells].join(", ");
}

export function withParameterToggled(
  state: UpdateSiteParamsState,
  parameter: UpdateParameter,
): UpdateSiteParamsState {
  const selectedAfterToggle = new Set(state.selectedParameters);
  if (selectedAfterToggle.has(parameter)) {
    selectedAfterToggle.delete(parameter);
  } else {
    selectedAfterToggle.add(parameter);
  }
  // Набор хранится в порядке ячеек строки, а не в порядке кликов.
  const selectedParameters = ALL_PARAMETERS.filter((candidate) => selectedAfterToggle.has(candidate));
  return { ...state, selectedParameters };
}

export function withRawData(state: UpdateSiteParamsState, rawData: string): UpdateSiteParamsState {
  return { ...state, rawData };
}

/**
 * Закрытие из ввода или отказа: при непустом поле — подтверждение потери ввода,
 * иначе диалог закрывается сразу.
 */
export function closeRequested(state: UpdateSiteParamsState): UpdateSiteParamsState {
  const hasRows = state.rawData.trim() !== "";
  return hasRows ? { ...state, request: "discard" } : CLOSED_UPDATE_STATE;
}

export function returnedToInput(state: UpdateSiteParamsState): UpdateSiteParamsState {
  return { ...state, request: "input", errorMessage: null };
}

export function sendingStarted(state: UpdateSiteParamsState): UpdateSiteParamsState {
  return { ...state, request: "sending", errorMessage: null };
}

export function succeeded(
  state: UpdateSiteParamsState,
  summary: UpdateParamsSummary,
): UpdateSiteParamsState {
  return { ...state, request: "result", summary, copyNotFoundStatus: "idle", copyInvalidStatus: "idle" };
}

export function failed(state: UpdateSiteParamsState, errorMessage: string): UpdateSiteParamsState {
  return { ...state, request: "error", errorMessage };
}

export function buildUpdateRequest(state: UpdateSiteParamsState): UpdateSiteParamsRequest {
  return { params: state.selectedParameters, rawData: state.rawData };
}

/** Текст буфера обмена списка ненайденных адресов — по одному адресу в строке. */
export function notFoundClipboardText(summary: UpdateParamsSummary): string {
  return summary.notFound.join("\n");
}

/** Текст буфера обмена невалидных строк — исходный текст каждой строки, как введён. */
export function invalidLinesClipboardText(summary: UpdateParamsSummary): string {
  return summary.invalidLines.map((line) => line.raw).join("\n");
}

interface CopyTargetDescriptor {
  clipboardText: (summary: UpdateParamsSummary) => string;
  withStatus: (state: UpdateSiteParamsState, status: CopyStatus) => UpdateSiteParamsState;
}

// Единственное место, различающее два списка итога: текст буфера обмена и поле
// статуса кнопки для каждого — обе точки ветвления (клик по кнопке, чтение статуса)
// читают отсюда, а не branch'ат по target сами.
const COPY_TARGETS: Record<CopyTarget, CopyTargetDescriptor> = {
  notFound: {
    clipboardText: notFoundClipboardText,
    withStatus: (state, status) => ({ ...state, copyNotFoundStatus: status }),
  },
  invalidLines: {
    clipboardText: invalidLinesClipboardText,
    withStatus: (state, status) => ({ ...state, copyInvalidStatus: status }),
  },
};

/** Текст буфера обмена одного из двух списков итога («Копирование списка итога»). */
export function copyTargetClipboardText(summary: UpdateParamsSummary, target: CopyTarget): string {
  return COPY_TARGETS[target].clipboardText(summary);
}

/** Статус «Скопировать» одного из двух списков итога после попытки записи в буфер. */
export function withCopyStatus(
  state: UpdateSiteParamsState,
  target: CopyTarget,
  status: CopyStatus,
): UpdateSiteParamsState {
  return COPY_TARGETS[target].withStatus(state, status);
}

/**
 * Причина отказа из тела ответа backend'а (`{ error }`); null, когда тело не JSON —
 * шлюз перед backend'ом отвечает на сбой HTML-страницей, и разбирать в ней нечего.
 */
export async function readRefusalReason(response: Response): Promise<string | null> {
  const contentType = response.headers.get("content-type");
  const isJsonBody = contentType !== null && contentType.includes("application/json");
  let reason: string | null = null;
  if (isJsonBody) {
    const body = (await response.json()) as { error: string };
    reason = body.error;
  }
  return reason;
}

/**
 * Причина отклонённого запроса: русская причина backend'а идёт на экран как есть,
 * а её отсутствие и общий английский текст зод-ветки заменяются классом причины.
 */
function rejectedRequestReason(reason: string | null): string {
  const isGenericReason = reason === null || reason === GENERIC_VALIDATION_REASON;
  return isGenericReason ? VALIDATION_REFUSAL_MESSAGE : reason;
}

/**
 * Сообщение отказа по причине: без пермишена — повтор не поможет; отклонённый
 * запрос — причина из ответа и следующий шаг; всё остальное — запись прервана.
 */
export function refusalMessage(status: number, reason: string | null): string {
  let message = TRANSACTION_ABORTED_MESSAGE;
  if (status === 403) {
    message = PERMISSION_REFUSAL_MESSAGE;
  } else if (status === 400) {
    message = rejectedRequestReason(reason) + REJECTED_LIST_NEXT_STEP;
  }
  return message;
}
